import { useEffect } from 'react';

import type { IAnswerCodeElement } from '@omq/answer-code-element';
import { getCombinedIntegrationClassName } from '../../utils/css-utils';

/**
 * Initialize code element for given code element.
 *
 * @param container - HTML code element.
 *
 *  @returns {Promise<Array<IAnswerCodeElement>>}
 */
export async function initializeAnswerEditors(
  container: HTMLElement,
): Promise<Array<IAnswerCodeElement>> {
  // get all code elements
  const codeElements = container.querySelectorAll(
    getCombinedIntegrationClassName('-answer-item.source'),
  );

  // filter out code elements that already have been initialized or are not visible (descendant of hidden element)
  // (we assume that if a code element contains a div with class "CodeMirror", it has already been initialized)
  const filteredCodeElements = Array.from(codeElements).filter((codeElement) => {
    // check if code element is visible (not descendant of hidden element)
    let currentElement: HTMLElement | null = codeElement as HTMLElement;
    while (currentElement) {
      const style = window.getComputedStyle(currentElement);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
      currentElement = currentElement.parentElement;
    }

    // check if code element already contains a CodeMirror instance
    return codeElement.querySelector('.CodeMirror') == null;
  });

  // return empty if there are no code elements
  if (filteredCodeElements.length === 0) {
    return [];
  }

  // import code element component
  const { AnswerCodeElement } = await import(
    /* webpackChunkName: "code-editor" */ '@omq/answer-code-element'
  );

  // initialize code element for all elements
  return Array.from(filteredCodeElements).map((codeElement) => {
    return new AnswerCodeElement(codeElement as HTMLElement);
  });
}

/**
 * Clean up editor instances
 *
 * @param editors
 */
function cleanUp(editors) {
  editors.forEach((editor) => editor.destroy());
}

/**
 * Hook to add code editor to all code elements.
 *
 * @param ref - reference to html element that contains the answer
 */
export function useAnswerCodeEditor(ref: { current: HTMLElement | null }) {
  useEffect(() => {
    if (ref.current == null) {
      return;
    }

    // add flag to detect if component is still mounted
    let isMounted = true;

    // store editor instances
    let editors = [];

    // initialize all editors
    initializeAnswerEditors(ref.current).then((result) => {
      // if component is not mounted anymore,
      // clean up
      if (!isMounted) {
        cleanUp(result);
        return;
      }

      // store editors for clean up later
      editors = result;
    });

    // clean up when component gets unmounted
    return () => {
      // update flag
      isMounted = false;

      // clean up editor instances
      cleanUp(editors);
    };
  }, [ref]);
}
